import Vue from 'vue';
import { filterDefectsByAiVisibility } from '@/features/defect-drawer/utils/defectFilter';
import { logExpandedDebug } from '@/utils/debug/expandedConsoleLog';
import { ExamineOutputService } from '../../../_services';
import DotSolarMap from '../../../components/map/DotSolarMap';
import userService from '../../../_services/user.service';
import { appPerfMark, appPerfMeasure, appPerfSinceMyProfile } from '@/utils/appBootstrapPerfLog.js';
import { DashboardApi } from '../../../apis/dashboard.api';

/** reportId별 in-flight Promise — 동시 중복 요청만 합친다(완료 후 재호출은 새 HTTP). */
const defectSummaryInflightByReportId = new Map();

export function __resetDefectSummaryInflightForTests() {
  defectSummaryInflightByReportId.clear();
}

function normalizeDefectSummaryReportId(reportId) {
  if (reportId == null || reportId === '') {
    return null;
  }
  const numeric = Number(reportId);
  return Number.isFinite(numeric) ? numeric : reportId;
}

function isDefectSummaryNotFoundError(error) {
  const body = error?.response?.data;
  if (typeof body === 'string') {
    return body.includes('Not exist data having');
  }
  if (body != null && typeof body.message === 'string') {
    return body.message.includes('Not exist data having');
  }
  return false;
}
// 객체에서 특정 속성을 제거하는 유틸리티 함수
function copyObjectWithoutProperties(obj, propertiesToRemove) {
  const newObj = { ...obj };
  propertiesToRemove.forEach(prop => {
    delete newObj[prop];
  });
  return newObj;
}

const state = {
  // 대시보드 핵심 상태
  projects: [],
  thermalImages: [],
  selectedThermalImage: null,
  selectedThermalImageIndex: -1,
  selectedThermalImageId: null,
  hoveredThermalImageIndex: null,
  showAiDefectFlag: false,
  aiDefectLoading: false,
  /** 열화상 목록 API(fetchThermalImages) 진행 중 — ImageExplorer 등에서 이전 목록 잔상 방지 */
  thermalImagesLoading: false,
  dashboardLoading: false,

  // 기타 대시보드 상태
  UTMKCoordinates: null,
  thermalContainers: [],
  comments: [],
  moduleDefects: [],
  wfsDefectRows: [],
  /** 이번 세션에 wfsDefectRows 가 로드된 reportId (ensureWfs 판별) */
  wfsDefectRowsReportId: null,
  /** 확정 결함 WFS 로드 중 — 모듈 리스트 skeleton */
  wfsDefectRowsLoading: false,
  images: [],
  leftDrawer: false,
  moduleLists: [],

  // ── Dashboard 결함 drawer (EventBus → Vuex) ──
  defectDrawerOpen: false,
  /** siteDesign Array→String→Module 계층 (모듈리스트 트리용) */
  siteDesignModuleHierarchy: [],
  /** siteDesign 모듈 피처 중심점 seq → [lon, lat] */
  moduleCentersBySeq: {},
  /** siteDesign 모듈 피처 링 seq → [[lon, lat], ...] */
  moduleRingsBySeq: {},
  /** siteDesign API 요청 중 — 모듈리스트 skeleton 로딩 표시 */
  siteDesignLoading: false,
  defectFlyTarget: null,
  mapHighlightTarget: null,
  moduleClickTarget: null,
  /** 지도 툴바 결함 유형 필터 — 모듈 리스트와 동기화 */
  toolbarDefectFilters: [],
  /** 지도 툴바 결함 요약 테이블 (/dashboard/defects/summary) */
  defectSummary: []
};

const getters = {
  // 대시보드 핵심 getters
  getThermalImages(state) {
    return state.thermalImages;
  },
  getShowAiDefectFlag(state) {
    return state.showAiDefectFlag;
  },
  projects(state) {
    return state.projects;
  },
  getAiDefectLoading(state) {
    return state.aiDefectLoading;
  },
  getThermalImagesLoading(state) {
    return state.thermalImagesLoading;
  },
  getDashboardLoading(state) {
    return state.dashboardLoading;
  },
  getSelectedThermalImage(state) {
    return state.selectedThermalImage;
  },
  getSelectedThermalImageIndex(state) {
    return state.selectedThermalImageIndex;
  },
  getSelectedThermalImageId(state) {
    return state.selectedThermalImageId;
  },
  getHoveredThermalImageIndex(state) {
    return state.hoveredThermalImageIndex;
  },
  canNavigatePrevious: state => {
    if (state.selectedThermalImageIndex === undefined || state.selectedThermalImageIndex === null) {
      return false;
    }
    return state.selectedThermalImageIndex > 0;
  },
  canNavigateNext: state => {
    if (state.selectedThermalImageIndex === undefined || state.selectedThermalImageIndex === null) {
      return false;
    }
    return state.selectedThermalImageIndex < state.thermalImages.length - 1;
  },
  getExamineDefectSummaries(state) {
    return state.defectSummary;
  }
};

const mutations = {
  // 대시보드 핵심 mutations
  setUTMKCoordinates: function (state, payload) {
    state.UTMKCoordinates = payload.coordinates;
  },

  setThermalContainers: function (state, payload) {
    state.thermalContainers = payload.thermalContainers;
  },

  setThermalImages: function (state, thermalImages) {
    state.thermalImages = thermalImages;
  },

  setComments: function (state, payload) {
    state.comments = payload;
  },

  setModuleDefects: function (state, payload) {
    state.moduleDefects = payload._embedded.moduleDefects;
  },

  setWfsDefectRows(state, rows) {
    state.wfsDefectRows = rows || [];
  },

  setWfsDefectRowsReportId(state, reportId) {
    state.wfsDefectRowsReportId =
      reportId == null || reportId === '' ? null : Number(reportId) || reportId;
  },

  clearWfsDefectRows(state) {
    state.wfsDefectRows = [];
    state.wfsDefectRowsReportId = null;
    state.wfsDefectRowsLoading = false;
  },

  setWfsDefectRowsLoading(state, loading) {
    state.wfsDefectRowsLoading = !!loading;
  },

  /** 낙관적 삭제 — UI에서 즉시 제거 */
  removeWfsDefectRow(state, seq) {
    const target = Number(seq);
    if (!Number.isFinite(target)) {
      return;
    }
    state.wfsDefectRows = state.wfsDefectRows.filter(d => Number(d.seq) !== target);
  },

  /** 삭제 API 실패 시 롤백 */
  insertWfsDefectRow(state, { row, index }) {
    if (!row) {
      return;
    }
    const rows = [...state.wfsDefectRows];
    const at =
      typeof index === 'number' && index >= 0 && index <= rows.length ? index : rows.length;
    rows.splice(at, 0, row);
    rows.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
    state.wfsDefectRows = rows;
  },

  patchWfsDefectRow(state, { seq, patch }) {
    const target = Number(seq);
    if (!Number.isFinite(target) || !patch || typeof patch !== 'object') {
      return;
    }
    const index = state.wfsDefectRows.findIndex(row => Number(row.seq) === target);
    if (index < 0) {
      return;
    }
    Vue.set(state.wfsDefectRows, index, {
      ...state.wfsDefectRows[index],
      ...patch
    });
  },

  /** 낙관적 생성 — 같은 index 행을 서버 응답으로 교체 (seq 변경 포함, 재정렬 없음) */
  replaceWfsDefectRow(state, { fromSeq, row }) {
    if (!row || typeof row !== 'object') {
      return;
    }
    const target = Number(fromSeq);
    if (!Number.isFinite(target)) {
      return;
    }
    const index = state.wfsDefectRows.findIndex(existing => Number(existing.seq) === target);
    if (index < 0) {
      return;
    }
    Vue.set(state.wfsDefectRows, index, {
      ...state.wfsDefectRows[index],
      ...row
    });
  },

  patchThermalImageModuleDefect(state, { defectId, patch }) {
    const target = Number(defectId);
    if (!Number.isFinite(target) || !patch || typeof patch !== 'object') {
      return;
    }
    for (let i = 0; i < state.thermalImages.length; i++) {
      const image = state.thermalImages[i];
      const defects = image?.moduleDefects;
      if (!Array.isArray(defects) || defects.length === 0) {
        continue;
      }
      const defectIndex = defects.findIndex(
        defect => Number(defect?.id ?? defect?.seq) === target
      );
      if (defectIndex < 0) {
        continue;
      }
      const nextDefects = [...defects];
      nextDefects[defectIndex] = { ...nextDefects[defectIndex], ...patch };
      Vue.set(state.thermalImages, i, { ...image, moduleDefects: nextDefects });
      break;
    }
  },

  setImages: function (state, payload) {
    state.images = payload;
  },

  toggleLeftDrawer: function (state) {
    state.leftDrawer = !state.leftDrawer;
  },

  setModuleLists: function (state, payload) {
    state.moduleLists = payload;
  },

  setProjects: function (state, projects) {
    Vue.set(state, 'projects', projects);
  },

  setShowAiDefectFlag: function (state, payload) {
    state.showAiDefectFlag = payload;
  },

  SET_AI_DEFECT_LOADING(state, loading) {
    Vue.set(state, 'aiDefectLoading', loading);
  },

  SET_THERMAL_IMAGES_LOADING(state, loading) {
    Vue.set(state, 'thermalImagesLoading', loading);
  },

  SET_DASHBOARD_LOADING(state, loading) {
    Vue.set(state, 'dashboardLoading', loading);
  },

  SET_SELECTED_THERMAL_IMAGE_INDEX(state, index) {
    state.selectedThermalImageIndex = index;
    // index가 유효하면 해당 이미지도 함께 설정
    if (index >= 0 && state.thermalImages && state.thermalImages.length > index) {
      // 순환 참조를 제거하기 위해 이미지 객체를 깊은 복사
      state.selectedThermalImage = state.thermalImages[index];
      state.selectedThermalImageId = state.thermalImages[index].id || null;
    } else {
      state.selectedThermalImage = null;
      state.selectedThermalImageId = null;
    }
  },

  SET_SELECTED_THERMAL_IMAGE(state, { image, index }) {
    // 순환 참조를 제거하기 위해 이미지 객체를 깊은 복사
    state.selectedThermalImage = image;
    state.selectedThermalImageIndex = index;
    state.selectedThermalImageId = image?.id || null;
  },

  SET_SELECTED_THERMAL_IMAGE_ID(state, id) {
    state.selectedThermalImageId = id;
  },

  SET_HOVERED_THERMAL_IMAGE_INDEX(state, index) {
    state.hoveredThermalImageIndex = index;
  },

  // 기존 코드 호환성을 위한 mutations
  updateImage: function (state, payload) {
    state.selectedThermalImage = payload.image;
    state.selectedThermalImageIndex = payload.index || -1;
  },

  selectedThermalImage: function (state, payload) {
    state.selectedThermalImage = payload;
    state.selectedThermalImageIndex = payload.imageIndex || -1;
  },

  // ── Defect drawer (EventBus → Vuex) ──
  setDefectDrawerOpen(state, open) { state.defectDrawerOpen = open; },
  setSiteDesignModuleHierarchy(state, hierarchy) {
    state.siteDesignModuleHierarchy = hierarchy || [];
  },
  setModuleCentersBySeq(state, centers) {
    state.moduleCentersBySeq = centers && typeof centers === 'object' ? centers : {};
  },
  setModuleRingsBySeq(state, rings) {
    state.moduleRingsBySeq = rings && typeof rings === 'object' ? rings : {};
  },
  setSiteDesignLoading(state, loading) {
    state.siteDesignLoading = !!loading;
  },
  setDefectFlyTarget(state, defect) { state.defectFlyTarget = defect; },
  setMapHighlightTarget(state, target) { state.mapHighlightTarget = target; },
  setModuleClickTarget(state, target) { state.moduleClickTarget = target; },
  setToolbarDefectFilters(state, filters) {
    state.toolbarDefectFilters = Array.isArray(filters) ? filters : [];
  },
  setDefectSummary(state, rows) {
    Vue.set(state, 'defectSummary', Array.isArray(rows) ? rows : []);
  }
};

const actions = {
  // 대시보드 핵심 actions
  searchProjects({ state }, keyword) {
    if (keyword && keyword.length) {
      const filteredProjects = [];

      state.projects.map(project => {
        // children 속성을 제거한 후 새 객체를 생성하고, children을 빈 배열로 초기화
        const tempProject = {
          ...copyObjectWithoutProperties(project, ['children']),
          children: []
        };
        project.children.map(site => {
          if (site.name.includes(keyword)) {
            tempProject.children.push(site);
          }
        });
        if (tempProject.children.length > 0) filteredProjects.push(tempProject);
      });
      return filteredProjects;
    } else {
      return state.projects;
    }
  },

  selectMissionPlan: function ({ commit }, payload) {
    commit('selectMissionPlan', payload);
  },

  init: function ({ commit }, payload) {
    // init 로직
  },

  /** 로그인·대시보드 진입 시 AI 결함 보기 off (이미지 리스트는 showAiDefect=false API 기준). */
  resetAiDefectViewState({ commit, dispatch, state }) {
    commit('setShowAiDefectFlag', false);
    commit('SET_AI_DEFECT_LOADING', false);
    const pruned = filterDefectsByAiVisibility(state.wfsDefectRows, false);
    if (pruned.length !== (state.wfsDefectRows?.length ?? 0)) {
      commit('setWfsDefectRows', pruned);
    }
    return dispatch('defect/setAiInclude', false, { root: true });
  },

  getUserDashboardData({ commit, dispatch }) {
    if (!userService || typeof userService.getMyDashboard !== 'function') {
      console.error('[DASHBOARD STORE] userService 또는 getMyDashboard가 없음');
      commit('SET_DASHBOARD_LOADING', false);
      return Promise.reject(new Error('UserService is not available'));
    }

    appPerfMark('dashboard:getUserDashboardData:start');
    appPerfSinceMyProfile('dashboard:getUserDashboardData:start');
    commit('SET_DASHBOARD_LOADING', true);
    return userService
      .getMyDashboard()
      .then(result => {
        appPerfMark('dashboard:getMyDashboard:response', {
          projectCount: result?.data?.projects?.length ?? null
        });
        const projects = result.data.projects;
        commit('setProjects', projects);
      })
      .then(() => {
        return dispatch('searchProjects');
      })
      .catch(error => {
        console.error('[DASHBOARD STORE] getMyDashboard 실패:', error);
        commit('setProjects', []);
      })
      .finally(() => {
        commit('SET_DASHBOARD_LOADING', false);
        appPerfMeasure('dashboard:getUserDashboardData:done', 'dashboard:getUserDashboardData:start');
        appPerfSinceMyProfile('dashboard:getUserDashboardData:done');
      });
  },

  async fetchThermalImages({ commit, state }, payload) {
    const normalizeThermalImageId = (id) =>
      id == null || id === '' ? null : id;

    const restoreThermalImageId = payload?.clearThermalSelection
      ? null
      : normalizeThermalImageId(payload?.restoreThermalImageId) ??
        normalizeThermalImageId(state.selectedThermalImageId) ??
        normalizeThermalImageId(state.selectedThermalImage?.id);

    appPerfMark('dashboard:fetchThermalImages:start', {
      reportId: payload?.reportId,
      restoreThermalImageId
    });
    appPerfSinceMyProfile('dashboard:fetchThermalImages:start', {
      reportId: payload?.reportId
    });
    commit('SET_THERMAL_IMAGES_LOADING', true);
    if (!restoreThermalImageId) {
      commit('setThermalImages', []);
      commit('SET_SELECTED_THERMAL_IMAGE_INDEX', -1);
    }
    try {
      const data = await ExamineOutputService.getThermalImages(
        payload.reportId,
        state.showAiDefectFlag
      );
      const images = data?.content || [];
      commit('setThermalImages', images);

      if (restoreThermalImageId) {
        const idx = images.findIndex(
          img => Number(img?.id) === Number(restoreThermalImageId)
        );
        if (idx >= 0) {
          commit('SET_SELECTED_THERMAL_IMAGE', { image: images[idx], index: idx });
        } else {
          commit('SET_SELECTED_THERMAL_IMAGE_INDEX', -1);
        }
      }
    } catch (error) {
      console.error('[dashboard] fetchThermalImages', error);
      if (!restoreThermalImageId) {
        commit('setThermalImages', []);
        commit('SET_SELECTED_THERMAL_IMAGE_INDEX', -1);
      }
    } finally {
      commit('SET_THERMAL_IMAGES_LOADING', false);
      appPerfMeasure('dashboard:fetchThermalImages:done', 'dashboard:fetchThermalImages:start', {
        reportId: payload?.reportId,
        imageCount: state.thermalImages?.length ?? 0
      });
      appPerfSinceMyProfile('dashboard:fetchThermalImages:done');
    }
  },

  async toggleAndFetchThermalData({ dispatch, rootState }, payload) {
    if (payload) {
      DotSolarMap.fetchAiDetectDefectLocation(rootState.explorer.report.id);
    } else {
      DotSolarMap.removeAiDetectDefectLocation();
    }

    return await dispatch(
      'dashboard/fetchThermalImages',
      {
        reportId: rootState.explorer.report.id
      },
      { root: true }
    );
  },

  async setShowAiDefectFlag({ dispatch, commit, rootState }, payload) {
    logExpandedDebug('[MAP-LAYER-DEBUG]', 'store action: setShowAiDefectFlag 진입', {
      payload,
      reportId: rootState.explorer.report?.id,
      beforeShowAiDefectFlag: rootState.dashboard?.showAiDefectFlag
    });
    // 1. Loading 상태 시작
    commit('SET_AI_DEFECT_LOADING', true);

    try {
      // 2. 플래그 설정 (dashboard store)
      commit('setShowAiDefectFlag', payload);

      // 2-1. defect store의 aiInclude도 동기화
      await dispatch('defect/setAiInclude', payload, { root: true });

      // 3. AI 결함 지도 표시/제거
      if (payload) {
        console.log('[MAP-LAYER-DEBUG] store action: fetchAiDetectDefectLocation 호출');
        DotSolarMap.fetchAiDetectDefectLocation(rootState.explorer.report.id);
      } else {
        console.log('[MAP-LAYER-DEBUG] store action: removeAiDetectDefectLocation 호출');
        DotSolarMap.removeAiDetectDefectLocation();
        const rows = rootState.dashboard?.wfsDefectRows || [];
        const pruned = filterDefectsByAiVisibility(rows, false);
        if (pruned.length !== rows.length) {
          commit('setWfsDefectRows', pruned);
          logExpandedDebug(
            '[MODULE-LIST-DEBUG]',
            'store: AI OFF → wfsDefectRows에서 AI 결함 제거',
            {
              before: rows.length,
              after: pruned.length,
              removed: rows.length - pruned.length
            }
          );
        }
      }

      // 4. 열화상 이미지 재로드 (AI 결함 포함 여부 반영)
      if (rootState.explorer.report?.id) {
        logExpandedDebug('[MAP-LAYER-DEBUG]', 'store action: fetchThermalImages 호출', {
          reportId: rootState.explorer.report.id,
          showAiDefect: payload
        });
        await dispatch(
          'dashboard/fetchThermalImages',
          {
            reportId: rootState.explorer.report.id
          },
          { root: true }
        );
        logExpandedDebug('[MAP-LAYER-DEBUG]', 'store action: setShowAiDefectFlag 완료', { payload });
        return;
      }
    } catch (error) {
      console.error('[DASHBOARD STORE] setShowAiDefectFlag 실패:', error);
      console.log('[MAP-LAYER-DEBUG] store action: setShowAiDefectFlag 실패', {
        payload,
        error: String(error)
      });
      throw error;
    } finally {
      // 5. Loading 상태 종료
      commit('SET_AI_DEFECT_LOADING', false);
    }
  },

  /**
   * 이미지 인덱스 변경 (기본 네비게이션)
   * @param {*} direction :  next, previous
   * @returns boolean
   */
  changeThermalImage({ commit, state }, direction) {
    if (state.selectedThermalImageIndex === undefined || state.selectedThermalImageIndex === null) {
      return false;
    }

    const currentIndex = state.selectedThermalImageIndex;
    let newIndex = currentIndex;
    const maxIndex = (state.thermalImages?.length || 0) - 1;

    if (direction === 'next' && currentIndex < maxIndex) {
      newIndex = currentIndex + 1;
    } else if (direction === 'previous' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    }

    if (newIndex !== currentIndex) {
      commit('SET_SELECTED_THERMAL_IMAGE_INDEX', newIndex);
      return true;
    }

    return false;
  },

  /**
   * 결함 정보 모달에서 이전/다음 버튼 클릭시 이미지 변경
   * @param {*} direction :  next, previous
   */
  async navigateToNewDefectDialog({ dispatch }, direction) {
    // 인덱스만 변경 — 결함 다이얼로그 동기화는 DefectInfoDialog의 selectedThermalImage.id watch
    return dispatch('changeThermalImage', direction);
  },

  /**
   * 지도 툴바 결함 요약 — 동일 reportId in-flight 중복 HTTP는 1회로 합친다.
   * @param {number|string|null|undefined} reportId
   * @returns {Promise<Array|undefined>}
   */
  async fetchDefectSummary({ commit }, reportId) {
    const normalizedReportId = normalizeDefectSummaryReportId(reportId);
    if (normalizedReportId == null) {
      return undefined;
    }

    const inflight = defectSummaryInflightByReportId.get(normalizedReportId);
    if (inflight) {
      return inflight;
    }

    const promise = DashboardApi.getDefectSummary(normalizedReportId)
      .then(resp => {
        const rows = resp?.data;
        commit('setDefectSummary', rows);
        return rows;
      })
      .catch(error => {
        if (isDefectSummaryNotFoundError(error)) {
          return undefined;
        }
        console.error('get defect summary, error', error);
        return undefined;
      })
      .finally(() => {
        if (defectSummaryInflightByReportId.get(normalizedReportId) === promise) {
          defectSummaryInflightByReportId.delete(normalizedReportId);
        }
      });

    defectSummaryInflightByReportId.set(normalizedReportId, promise);
    return promise;
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
